import React, { useEffect, useRef, useState } from 'react';
import { supabase } from '../lib/supabase';
import { format } from 'date-fns';
import { Booking, Screening, Movie } from '../types';
import { BrowserQRCodeReader } from '@zxing/browser';
import { Camera, RefreshCw, Upload, StopCircle, Trash2, ScanLine, CheckCircle2, XCircle, ArrowLeft } from 'lucide-react';

export default function TicketValidatorPage({ onClose }: { onClose: () => void }) {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const readerRef = useRef<BrowserQRCodeReader | null>(null);
  const [userRole, setUserRole] = useState<string | null>(null);
  const [scannedText, setScannedText] = useState<string | null>(null);
  type ValidateResponse = { ok?: boolean; payload?: { booking_id: number; email: string; iat: number; exp: number }; error?: string; message?: string };
  const [result, setResult] = useState<ValidateResponse | null>(null);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [errorCode, setErrorCode] = useState<string | null>(null);
  const [validating, setValidating] = useState(false);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [cameraActive, setCameraActive] = useState(false);
  const [captureInProgress, setCaptureInProgress] = useState(false);
  const [confirmRedeemOpen, setConfirmRedeemOpen] = useState(false);
  const [redeeming, setRedeeming] = useState(false);
  const bookingIdFromResult = result?.payload?.booking_id ?? null;
  const [bookingRow, setBookingRow] = useState<(Booking & { screenings?: Screening & { movies?: Movie } }) | null>(null);
  const [seatIds, setSeatIds] = useState<string[]>([]);
  const [detailsError, setDetailsError] = useState<string | null>(null);
  const [isRedeemed, setIsRedeemed] = useState<boolean>(false);
  const [validatedBy, setValidatedBy] = useState<{ id?: string; email?: string } | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [capturedFrameUrl, setCapturedFrameUrl] = useState<string | null>(null);
  const pendingStreamRef = useRef<MediaStream | null>(null);

  useEffect(() => {
    supabase.auth.getUser().then(async ({ data }) => {
      const uid = data.user?.id;
      if (!uid) {
        setUserRole(null);
        return;
      }
      const { data: profile } = await supabase.from('profiles').select('role').eq('id', uid).maybeSingle();
      const r = (profile as { role?: string } | null)?.role || null;

      if (r !== 'admin' && r !== 'organizer' && r !== 'validator') {
        const { count: orgValCount } = await supabase
          .from('organizer_validators')
          .select('*', { count: 'exact', head: true })
          .eq('user_id', uid);

        const { count: screenValCount } = await supabase
          .from('screening_validators')
          .select('*', { count: 'exact', head: true })
          .eq('user_id', uid)
          .eq('active', true);

        if ((orgValCount || 0) > 0 || (screenValCount || 0) > 0) {
          setUserRole('validator');
          return;
        }
      }
      setUserRole(r);
    });
  }, []);

  useEffect(() => {
    const current = videoRef.current;
    return () => {
      const stream = current?.srcObject as MediaStream | null;
      if (stream) {
        stream.getTracks().forEach(t => t.stop());
      }
    };
  }, []);

  // Attach the pending stream once the <video> element mounts (after cameraActive becomes true)
  useEffect(() => {
    if (cameraActive && videoRef.current && pendingStreamRef.current) {
      videoRef.current.srcObject = pendingStreamRef.current;
      pendingStreamRef.current = null;
      videoRef.current.play().catch(() => {});
    }
  }, [cameraActive]);

  useEffect(() => {
    if (!scannedText) return;
    setValidating(true);
    setErrorMsg(null);
    setErrorCode(null);
    setResult(null);
    console.log('Validating QR:', scannedText);
    supabase.functions.invoke<ValidateResponse>('validate-qr', { body: { qr: scannedText } })
      .then(async ({ data, error }) => {
        if (error) {
          console.log('Validation error object:', error);
          // Try to extract debug info from the response context
          if ('context' in error && typeof (error as any).context?.json === 'function') {
            try {
              const body = await (error as any).context.json();
              console.log('Validation error body (DEBUG INFO):', body);
              if (body?.debug) {
                console.log('Detailed Debug:', body.debug);
              }
              if (body?.error) {
                setErrorCode(body.error);
                if (body.error === 'cancelled') {
                  setErrorMsg('Booking is not confirmed. Only confirmed bookings can be validated.');
                  return;
                }
              }
            } catch (e) {
              console.log('Failed to parse error context body', e);
            }
          }
        }
        console.log('Validation response:', { data, error });
        if (error) {
          setErrorMsg(error.message || 'Validation failed');
        } else if (data?.error) {
          const code = data.error;
          setErrorCode(code);
          if (code === 'no_event_permissions') setErrorMsg('You do not have permissions for this event. Contact the organizer.');
          else if (code === 'forbidden') setErrorMsg('You do not have permission to validate tickets.');
          else if (code === 'expired') setErrorMsg('QR code has expired.');
          else if (code === 'redeemed') setErrorMsg('Ticket already redeemed.');
          else if (code === 'unsupported_format') setErrorMsg('Unsupported QR format. Please scan the issued ticket QR.');
          else if (code === 'invalid_qr') setErrorMsg('Invalid QR code data. Please rescan or upload a clear image.');
          else if (code === 'malformed_payload') setErrorMsg('QR payload is malformed. Ask the organizer to reissue QR.');
          else if (code === 'qr_missing') setErrorMsg('No QR record found for this booking. Issue a QR first.');
          else if (code === 'cancelled') setErrorMsg('Booking is not confirmed. Only confirmed bookings can be validated.');
          else if (code === 'not_found') setErrorMsg('Booking not found. Please verify the ticket and try again.');
          else setErrorMsg('Validation failed.');
        } else {
          setResult(data);
        }
      })
      .catch((e) => {
        console.error('Validation exception:', e);
        setErrorMsg(e instanceof Error ? e.message : 'Validation failed');
      })
      .finally(() => setValidating(false));
  }, [scannedText]);

  useEffect(() => {
    const bid = result?.payload?.booking_id ?? null;
    const ok = !!result?.ok;
    if (!ok || !bid) {
      setBookingRow(null);
      setSeatIds([]);
      setDetailsError(null);
      return;
    }
    setDetailsError(null);
    (async () => {
      const { data: bookingData, error: bookingErr } = await supabase
        .from('bookings')
        .select(`*, screenings (*, movies (*))`)
        .eq('id', bid)
        .maybeSingle();
      if (bookingErr) {
        setDetailsError('Unable to load booking details');
        setBookingRow(null);
      } else {
        setBookingRow(bookingData as unknown as (Booking & { screenings?: Screening & { movies?: Movie } }));
      }
      const { data: seatRow } = await supabase
        .from('booking_seats')
        .select('seat_ids')
        .eq('booking_id', bid)
        .maybeSingle();
      setSeatIds(((seatRow as { seat_ids?: string[] } | null)?.seat_ids) || []);
      const { data: qrRow } = await supabase
        .from('booking_qr_codes')
        .select('redeemed, validated_by')
        .eq('booking_id', bid)
        .maybeSingle();
      const redeemed = Boolean((qrRow as { redeemed?: boolean } | null)?.redeemed)
      setIsRedeemed(redeemed);
      const vId = (qrRow as { validated_by?: string } | null)?.validated_by || undefined;
      if (vId) {
        const { data: vProf } = await supabase
          .from('profiles')
          .select('email')
          .eq('id', vId)
          .maybeSingle();
        setValidatedBy({ id: vId, email: (vProf as { email?: string } | null)?.email || undefined });
      } else {
        setValidatedBy(null);
      }
    })();
  }, [result?.ok, result?.payload?.booking_id]);

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (cameraActive) stopCamera();
    const url = URL.createObjectURL(file);
    setImagePreview(url);
    setErrorMsg(null);
    setResult(null);
    const img = new Image();
    img.src = url;
    img.onload = async () => {
      try {
        readerRef.current = readerRef.current || new BrowserQRCodeReader();
        type ZXResult = { getText: () => string };
        let res: ZXResult | null = null;
        try {
          res = (await readerRef.current.decodeFromImageElement(img)) as ZXResult;
        } catch {
          res = null;
        }
        if (!res) {
          try {
            res = (await readerRef.current.decodeFromImageUrl(url)) as ZXResult;
          } catch {
            res = null;
          }
        }
        if (res) {
          const text = res.getText().trim();
          setScannedText(null);
          setTimeout(() => setScannedText(text), 0);
        } else {
          const remote = await decodeViaQrServer(file);
          if (remote) {
            setScannedText(null);
            setTimeout(() => setScannedText(remote.trim()), 0);
          } else {
            setErrorMsg('No QR found in image');
          }
        }
      } catch {
        setErrorMsg('No QR found in image');
      } finally {
        URL.revokeObjectURL(url);
      }
    };
    img.onerror = () => {
      setErrorMsg('Unable to read image');
      URL.revokeObjectURL(url);
    };
  };

  const decodeViaQrServer = async (file: File): Promise<string | null> => {
    const form = new FormData();
    form.append('file', file, file.name);
    try {
      const resp = await fetch('https://api.qrserver.com/v1/read-qr-code/', { method: 'POST', body: form });
      if (!resp.ok) return null;
      type QrServerSymbol = { data: string | null; error: string | null };
      type QrServerItem = { symbol: QrServerSymbol[] };
      const json = (await resp.json()) as QrServerItem[];
      const data = json?.[0]?.symbol?.[0]?.data || null;
      return data && data.length ? data : null;
    } catch {
      return null;
    }
  };

  const startCamera = async () => {
    setErrorMsg(null);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: 'environment' } });
      if (videoRef.current) {
        // Video element already in DOM (e.g. re-starting after stop)
        videoRef.current.srcObject = stream;
        try { await videoRef.current.play(); } catch { }
      } else {
        // Video element not yet mounted; stash stream for the useEffect to attach
        pendingStreamRef.current = stream;
      }
      setCameraActive(true);
    } catch {
      setErrorMsg('Unable to access camera');
    }
  };

  const stopCamera = () => {
    try {
      readerRef.current?.reset();
    } catch { }
    const stream = videoRef.current?.srcObject as MediaStream | null;
    if (stream) {
      try {
        stream.getTracks().forEach(t => t.stop());
      } catch { }
    }
    if (videoRef.current) {
      try { videoRef.current.pause(); } catch { }
      videoRef.current.srcObject = null;
    }
    setCaptureInProgress(false);
    setCameraActive(false);
  };

  const captureAndDecodeOnline = async () => {
    if (!videoRef.current) return;
    setCaptureInProgress(true);
    setErrorMsg(null);
    setResult(null);
    const video = videoRef.current;
    const width = video.videoWidth || 640;
    const height = video.videoHeight || 480;
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d');
    if (!ctx) {
      setErrorMsg('Unable to capture frame');
      setCaptureInProgress(false);
      return;
    }
    ctx.drawImage(video, 0, 0, width, height);
    canvas.toBlob(async (blob) => {
      if (!blob) {
        setErrorMsg('Unable to capture frame');
        setCaptureInProgress(false);
        return;
      }
      const prev = capturedFrameUrl;
      const url = URL.createObjectURL(blob);
      if (prev) URL.revokeObjectURL(prev);
      setCapturedFrameUrl(url);
      const file = new File([blob], 'capture.jpg', { type: 'image/jpeg' });
      const decoded = await decodeViaQrServer(file);
      if (decoded) {
        setScannedText(null);
        setTimeout(() => setScannedText(decoded.trim()), 0);
      } else {
        setErrorMsg('No QR found in captured frame');
      }
      setCaptureInProgress(false);
    }, 'image/jpeg', 0.92);
  };

  return (
    <div className="min-h-screen bg-white dark:bg-gray-900 pt-24">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">Validate Tickets</h1>
          <button aria-label="Back" onClick={onClose} className="h-10 w-10 bg-gray-200 text-gray-900 rounded hover:bg-gray-300 dark:bg-gray-700 dark:text-white dark:hover:bg-gray-600 inline-flex items-center justify-center">
            <ArrowLeft className="h-5 w-5" />
          </button>
        </div>

        {userRole !== 'admin' && userRole !== 'editor' && userRole !== 'organizer' && userRole !== 'validator' ? (
          <p className="text-red-400">You do not have permission to access this page.</p>
        ) : (
          <div className={cameraActive ? "grid grid-cols-1 lg:grid-cols-2 gap-8" : "grid grid-cols-1 gap-8"}>
            {cameraActive && (
              <div className="relative bg-black rounded-lg overflow-hidden h-[70vh]">
                <video ref={videoRef} autoPlay muted playsInline className="absolute inset-0 w-full h-full object-cover" />
                <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                  <div className="w-64 h-64 border-4 border-indigo-500 rounded-md"></div>
                </div>
              </div>
            )}

            <div className="bg-white dark:bg-gray-800 rounded-lg p-6">
              <div className="flex items-center gap-3 mb-4">
                {!cameraActive ? (
                  <button aria-label="Start Camera" onClick={startCamera} className="h-10 w-10 bg-indigo-600 text-white rounded hover:bg-indigo-700 inline-flex items-center justify-center">
                    <Camera className="h-5 w-5" />
                  </button>
                ) : (
                  <>
                    <button aria-label="Capture & Decode" onClick={captureAndDecodeOnline} disabled={captureInProgress} className="h-10 w-10 bg-indigo-600 text-white rounded hover:bg-indigo-700 disabled:opacity-60 inline-flex items-center justify-center">
                      <ScanLine className="h-5 w-5" />
                    </button>
                    <button aria-label="Retry" onClick={captureAndDecodeOnline} disabled={captureInProgress} className="h-10 w-10 bg-gray-200 text-gray-900 rounded hover:bg-gray-300 disabled:opacity-60 dark:bg-gray-700 dark:text-white dark:hover:bg-gray-600 inline-flex items-center justify-center">
                      <RefreshCw className="h-5 w-5" />
                    </button>
                    <button aria-label="Stop Camera" onClick={stopCamera} className="h-10 w-10 bg-gray-200 text-gray-900 rounded hover:bg-gray-300 dark:bg-gray-700 dark:text-white dark:hover:bg-gray-600 inline-flex items-center justify-center">
                      <StopCircle className="h-5 w-5" />
                    </button>
                  </>
                )}
                <label aria-label="Upload Image" className="h-10 w-10 bg-gray-200 text-gray-900 rounded cursor-pointer hover:bg-gray-300 dark:bg-gray-700 dark:text-white dark:hover:bg-gray-600 inline-flex items-center justify-center">
                  <Upload className="h-5 w-5" />
                  <input ref={fileInputRef} type="file" accept="image/*" onChange={handleImageUpload} className="hidden" />
                </label>
                {imagePreview && (
                  <button
                    onClick={() => {
                      setImagePreview(null);
                      setScannedText(null);
                      setResult(null);
                      setErrorMsg(null);
                      setErrorCode(null);
                      if (fileInputRef.current) fileInputRef.current.value = "";
                    }}
                    aria-label="Clear Image"
                    className="h-10 w-10 bg-gray-200 text-gray-900 rounded hover:bg-gray-300 dark:bg-gray-700 dark:text-white dark:hover:bg-gray-600 inline-flex items-center justify-center"
                  >
                    <Trash2 className="h-5 w-5" />
                  </button>
                )}
              </div>
              {imagePreview && (
                <div className="mb-4">
                  <img src={imagePreview} alt="Uploaded" className="w-full max-h-64 object-contain rounded" />
                </div>
              )}
              {capturedFrameUrl && (
                <div className="mb-4">
                  <img src={capturedFrameUrl} alt="Captured" className="w-full max-h-64 object-contain rounded" />
                  <div className="mt-2">
                    <button
                      onClick={() => {
                        if (capturedFrameUrl) URL.revokeObjectURL(capturedFrameUrl);
                        setCapturedFrameUrl(null);
                      }}
                      className="px-3 py-2 bg-gray-200 text-gray-900 rounded hover:bg-gray-300 dark:bg-gray-700 dark:text-white dark:hover:bg-gray-600 inline-flex items-center gap-2"
                    >
                      <Trash2 className="h-4 w-4" />
                      Clear Frame
                    </button>
                  </div>
                </div>
              )}
              {!scannedText && (
                <p className="text-gray-700 dark:text-gray-300">Place the QR anywhere in view; the square is a guide.</p>
              )}
              {validating && (
                <p className="text-indigo-600 dark:text-indigo-400 mt-2">Validating...</p>
              )}

              {errorCode === 'cancelled' ? (
                <div className="mt-6 p-6 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl flex flex-col items-center text-center animate-in fade-in zoom-in duration-300">
                  <div className="h-16 w-16 bg-red-100 dark:bg-red-900/50 rounded-full flex items-center justify-center mb-4">
                    <XCircle className="h-8 w-8 text-red-600 dark:text-red-400" />
                  </div>
                  <h3 className="text-xl font-bold text-red-700 dark:text-red-400 mb-2">Ticket Cancelled</h3>
                  <p className="text-red-600 dark:text-red-300 max-w-xs mx-auto">
                    This ticket has been cancelled and is no longer valid for entry.
                  </p>
                </div>
              ) : errorMsg && (
                <div className="mt-3 rounded border border-red-700 bg-red-900/40 p-3 text-red-200">
                  {errorMsg}
                </div>
              )}

              <div className="mt-8">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">Booking Details</h3>
                {detailsError ? (
                  <p className="text-red-600 dark:text-red-400">{detailsError}</p>
                ) : bookingRow ? (
                  <div className="bg-gray-50 dark:bg-gray-900/60 border border-gray-200 dark:border-gray-700 rounded-lg p-4">
                    <div className="mb-3 space-y-1">
                      {isRedeemed ? (
                        <span className="inline-block px-2 py-1 text-xs font-medium rounded bg-yellow-600 text-white">Redeemed</span>
                      ) : (
                        <span className="inline-block px-2 py-1 text-xs font-medium rounded bg-green-600 text-white">Not Redeemed</span>
                      )}
                      {isRedeemed && validatedBy && (
                        <p className="text-xs text-gray-700 dark:text-gray-300">
                          Validated by: {validatedBy.email || validatedBy.id}
                        </p>
                      )}
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-gray-900 dark:text-gray-200">
                      <div>
                        <p className="text-xs text-gray-500 dark:text-gray-400">Booking ID</p>
                        <p className="text-sm font-medium">{bookingRow.id}</p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500 dark:text-gray-400">Email</p>
                        <p className="text-sm font-medium">{result?.payload?.email}</p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500 dark:text-gray-400">Customer Name</p>
                        <p className="text-sm font-medium">{(bookingRow as any).customer_name || (result?.payload?.email || '').split('@')[0]}</p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500 dark:text-gray-400">Seats</p>
                        <p className="text-sm font-medium">{bookingRow.seats}</p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500 dark:text-gray-400">Movie</p>
                        <p className="text-sm font-medium">{bookingRow.screenings?.movies?.title || '—'}</p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500 dark:text-gray-400">Screening Time</p>
                        <p className="text-sm font-medium">{bookingRow.screenings?.start_time ? format(new Date(bookingRow.screenings.start_time), 'PPpp') : '—'}</p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500 dark:text-gray-400">Location</p>
                        <p className="text-sm font-medium">{bookingRow.screenings?.location || '—'}</p>
                      </div>
                    </div>
                    {seatIds.length > 0 && (
                      <div className="mt-4">
                        <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">Seats</p>
                        <div className="flex flex-wrap gap-2">
                          {seatIds.map((s) => (
                            <span key={s} className="px-2 py-1 bg-indigo-600 text-white text-xs rounded">{s}</span>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <p className="text-gray-700 dark:text-gray-300">Details will appear after validation.</p>
                )}
              </div>

              {(cameraActive || imagePreview) && (
                <div className="mt-6 flex gap-3">
                  <button
                    onClick={() => {
                      setScannedText(null);
                      setResult(null);
                      setErrorMsg(null);
                      setErrorCode(null);
                      setIsRedeemed(false);
                      if (!readerRef.current) {
                        readerRef.current = new BrowserQRCodeReader();
                      }
                      if (!cameraActive) {
                        startCamera();
                      }
                      readerRef.current?.decodeFromConstraints({ video: { facingMode: 'environment' } }, videoRef.current!, (res) => {
                        if (res) {
                          const t = res.getText().trim();
                          setScannedText(null);
                          setTimeout(() => setScannedText(t), 0);
                          readerRef.current?.reset();
                        }
                      });
                    }}
                    className="px-4 py-2 bg-indigo-600 text-white rounded hover:bg-indigo-700 inline-flex items-center gap-2"
                  >
                    <ScanLine className="h-4 w-4" />
                    Scan Again
                  </button>
                  <button
                    onClick={() => setConfirmRedeemOpen(true)}
                    disabled={!bookingIdFromResult || !!errorMsg || !result?.ok || redeeming || isRedeemed}
                    className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-60 inline-flex items-center gap-2"
                  >
                    <CheckCircle2 className="h-4 w-4" />
                    Validate
                  </button>
                </div>
              )}

            </div>
          </div>
        )}
      </div>
      {confirmRedeemOpen && (
        <div className="fixed inset-0 bg-black/60 z-[120] flex items-center justify-center p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-md p-6">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">Confirm Validation</h2>
            <p className="text-gray-700 dark:text-gray-300 mb-6">Are you sure you want to mark this ticket as redeemed?</p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setConfirmRedeemOpen(false)}
                aria-label="Cancel"
                className="h-10 w-10 bg-gray-200 text-gray-900 rounded hover:bg-gray-300 dark:bg-gray-700 dark:text-white dark:hover:bg-gray-600 inline-flex items-center justify-center"
              >
                <XCircle className="h-5 w-5" />
              </button>
              <button
                onClick={async () => {
                  if (!bookingIdFromResult) return;
                  setRedeeming(true);
                  setErrorMsg(null);
                  try {
                    const { data, error } = await supabase.functions.invoke<{ ok?: boolean; error?: string; message?: string }>('redeem-qr', { body: { booking_id: bookingIdFromResult } });
                    if (error) {
                      setErrorMsg(error.message || 'Failed to redeem');
                    } else if (!data?.ok) {
                      if (data?.error === 'no_event_permissions') setErrorMsg('You do not have permissions for this event. Contact the organizer.');
                      else setErrorMsg('Failed to redeem');
                    } else {
                      setResult(prev => prev ? { ...prev } : null);
                      setIsRedeemed(true);
                    }
                  } finally {
                    setRedeeming(false);
                    setConfirmRedeemOpen(false);
                  }
                }}
                disabled={redeeming}
                aria-label="Validate"
                className="h-10 w-10 bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-60 inline-flex items-center justify-center"
              >
                <CheckCircle2 className="h-5 w-5" />
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
